import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import projectTaskService from '../../services/ProjectTaskService';
import ProjectTaskEditorTemplate from './ProjectTaskEditorTemplate';
import projectService from '../../services/ProjectService';
import taskTypeService from '../../services/TaskTypeService';
import vehicleService from '../../services/VehicleService';
import userService from '../../services/UserService';
import {toString} from '../../util/DateConverter';
import {useTitle} from '../dashboard/TitleContext';
import {useSnackbar} from '../../util/SnackbarProvider';
import taskService from "../../services/TaskService";

const priorities = [
    {id: "EXTRA_IMPORTANT", name: "Extra Fontos"},
    {id: "IMPORTANT", name: "Fontos"},
    {id: "NORMAL", name: "Normál"},
    {id: "NOT_IMPORTANT", name: "Nem Fontos"}
]

const statuses = [
    {id: "NOT_STARTED", name: "Nincs elkezdve"},
    {id: "IN_PROGRESS", name: "Folyamatban"},
    {id: "BLOCKED", name: "Elakadva"},
    {id: "OVERDUE", name: "Csúszásban"},
    {id: "DONE", name: "Befejezve"}
]

const ProjectTaskEditor = () => {

    const [waitingForServer, setWaitingForServer] = useState(false)
    const [nonFinishedOnly, setNonFinishedOnly] = useState(true)

    const urlParts = window.location.pathname.split('/')
    const projectId = urlParts[4]
    const id = urlParts[6] === 'create' ? undefined : urlParts[6]
    const {sendAlertMessage} = useSnackbar()

    const [projectTask, setProjectTask] = useState({
        users: [],
        priority: "NORMAL",
        status: "NOT_STARTED",
        progressLevel: "0"
    });

    const [projectName, setProjectName] = useState('Projekt');

    useEffect(() => {
        projectService.findById(projectId)
            .then(response => setProjectName(response.name))
            .catch((error) => console.error(error));
    }, [projectId]);

    useTitle([
        {name: 'Projekt Feladatok', url: '/dashboard/project-tasks/projects'},
        {name: projectName || 'Projekt', url: '/dashboard/project-tasks/projects/' + projectId},
        {
            name: projectTask.taskType?.name || (id ? 'Feladat' : 'Új Feladat'),
            url: '/dashboard/project-tasks/projects/' + projectId + '/tasks/' + (id || 'create')
        }
    ], projectName)

    const [message, setMessage] = useState('');
    const navigate = useNavigate()

    const [users, setUsers] = useState([]);
    useEffect(() => {
        userService.getSelectors()
            .then(response => setUsers(response))
            .catch(error => console.error(error))
    }, []);

    const [projects, setProjects] = React.useState([]);
    useEffect(() => {
        projectService.getSelectors()
            .then(response => setProjects(response))
            .catch(error => console.error(error))
    }, []);

    const [taskTypes, setTaskTypes] = useState([]);
    useEffect(() => {
        taskTypeService.getSelectors()
            .then(response => setTaskTypes(response))
            .catch(error => console.error(error));
    }, []);

    const [vehicles, setVehicles] = useState([]);
    useEffect(() => {
        vehicleService.getSelectors()
            .then(response => setVehicles(response))
            .catch(error => console.error(error))
    }, []);

    useEffect(() => {
        retrieveProjectTask()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, users, projects, vehicles, taskTypes, nonFinishedOnly]);

    function retrieveProjectTask() {
        if (!id) return
        projectTaskService.findById(id, nonFinishedOnly)
            .then(response => {
                const tasks = response.tasks.map(task => ({
                    ...task,
                    status: statuses.find(s => s.id === task.status)?.name,
                    priority: priorities.find(s => s.id === task.priority)?.name
                }));
                setProjectTask((prevProjectTask) => ({
                    ...prevProjectTask,
                    ...response,
                    tasks: tasks
                }));
            })
            .catch(error => console.error(error));
    }

    const onChange = (event) => {
        const {name, value} = event.target;
        setProjectTask((prevProjectTask) => ({
            ...prevProjectTask,
            [name]: value,
        }));
    };

    const onChangeCheckbox = (event) => {
        const {checked} = event.target;
        setNonFinishedOnly(checked)
    };

    const setDate = (newValue, name) => {
        setProjectTask((prevProjectTask) => ({
            ...prevProjectTask,
            [name]: newValue,
        }));
    };

    const onChangeSelectItem = (newValue, name) => {
        setProjectTask((prevProjectTask) => ({
            ...prevProjectTask,
            [name]: newValue
        }));
    }

    const create = (e) => {
        e.preventDefault();
        setWaitingForServer(true)
        projectTaskService.createProjectTask(projectId, prepareRequest())
            .then(() => {
                navigate(-1);
                sendAlertMessage('Feladat sikeresen létrehozva')
            })
            .catch((error) => setMessage(error.message))
            .finally(() => setWaitingForServer(false))
    };

    const update = (e) => {
        e.preventDefault();
        setWaitingForServer(true)
        projectTaskService.updateProjectTask(id, prepareRequest())
            .then(() => {
                navigate(-1);
                sendAlertMessage('Feladat sikeresen mentve')
            })
            .catch(error => setMessage(error.message))
            .finally(() => setWaitingForServer(false))
    };

    const deleteTask = (dialogState) => {
        setProjectTask((prevProjectTask) => ({...prevProjectTask, tasks: null}));
        taskService.delete(dialogState.object?.id)
            .then(() => {
                retrieveProjectTask()
                sendAlertMessage('Feladat sikeresen törölve')
            })
            .catch(error => sendAlertMessage(error?.message, 'error'))
    }

    const prepareRequest = () => {
        const copy = structuredClone(projectTask)
        if (projectTask.taskType) copy.taskTypeId = projectTask.taskType.id
        if (projectTask.vehicle) copy.vehicleId = projectTask.vehicle.id
        copy.deadline = toString(projectTask.deadline)
        copy.scheduleDateStart = toString(projectTask.scheduleDateStart)
        copy.scheduleDateEnd = toString(projectTask.scheduleDateEnd)
        copy.userIds = projectTask.users.map(user => user.id)
        delete copy.taskType
        delete copy.vehicle
        delete copy.users
        return copy
    }

    const onUsersChange = (event) => {
        const {value} = event.target
        const newSelection = typeof value === 'string' ? value.split(',') : value;
        const uniqueSelection = newSelection.reduce((acc, user) => {
            const exists = acc.find(item => item.id === user.id);
            if (exists) return acc.filter(item => item.id !== user.id);
            else return [...acc, user];
        }, []);

        setProjectTask((prev) => ({
            ...prev,
            users: uniqueSelection,
        }));
    }

    const copyProjectTask = (projectId) => {
        projectTaskService.copyProjectTask({projectTaskId: id, projectId: projectId})
            .then(() => sendAlertMessage('Feladat másolása sikeres'))
            .catch((error) => console.error(error))
    }

    return (
        <ProjectTaskEditorTemplate
            data={{
                id,
                projectId,
                projectTask,
                message,
                projects,
                taskTypes,
                vehicles,
                users,
                onChange,
                onChangeCheckbox,
                onUsersChange,
                deleteTask,
                setDate,
                onChangeSelectItem,
                create,
                update,
                waitingForServer,
                copyProjectTask,
                nonFinishedOnly
            }}
        />
    );
};

export default ProjectTaskEditor;
